import React, { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/Login";
import CadastroPrestadorPublico from "./components/CadastroPrestadorPublico";
import EsqueciSenha from "./components/EsqueciSenha";
import RedefinirSenha from "./components/RedefinirSenha";
import { ThemeProvider } from "./contexts/ThemeContext";
import ThemeToggle from "./components/ThemeToggle";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Toaster } from "./components/ui/sonner";
import Prestadores from "./pages/Prestadores";
import Diarias from "./pages/Diarias";
import Relatorios from "./pages/Relatorios";
import Custos from "./pages/Custos";
import Backups from "./pages/Backups";
import Dashboard from "./pages/Dashboard";
import Clientes from "./pages/Clientes";
import Funcoes from "./pages/Funcoes";
import Precos from "./pages/Precos";
import Auditoria from "./pages/Auditoria";
import Usuarios from "./pages/Usuarios";
import Blacklist from "./pages/Blacklist";
import Empresa from "./pages/Empresa";
import { Users, UserCheck, Briefcase, Calculator, CalendarDays, DollarSign, FileText, BarChart3, Menu, X, LogOut, Shield, User, Database, Building2 } from "lucide-react";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !isAdmin()) {
    return (
      <div className="p-6 text-center">
        <Shield className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Acesso Restrito</h2>
        <p className="text-gray-600 dark:text-gray-300">Você não tem permissão para acessar esta página.</p>
      </div>
    );
  }
  
  return children;
};

// Layout Component
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isAdmin, isOperacional } = useAuth();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: BarChart3 },
    { name: 'Diárias', href: '/diarias', icon: CalendarDays, adminOnly: false },
    { name: 'Relatórios', href: '/relatorios', icon: FileText, adminOnly: false },
    { name: 'Custos', href: '/custos', icon: DollarSign, adminOnly: true },
    { name: 'Auditoria', href: '/auditoria', icon: Shield, adminOnly: true },
    { name: 'Usuários', href: '/usuarios', icon: User, adminOnly: true },
    { name: 'Blacklist', href: '/blacklist', icon: X, adminOnly: true },
    { name: 'Prestadores', href: '/prestadores', icon: UserCheck, adminOnly: true },
    { name: 'Funções', href: '/funcoes', icon: Briefcase, adminOnly: true },
    { name: 'Preços', href: '/precos', icon: Calculator, adminOnly: true },
    { name: 'Clientes', href: '/clientes', icon: Users, adminOnly: true },
    { name: 'Backups', href: '/backups', icon: Database, adminOnly: true },
    { name: 'Dados da Empresa', href: '/empresa', icon: Building2, adminOnly: true },
  ];

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item => {
    // Operacional: acesso somente ao cadastro de Prestadores
    if (isOperacional()) {
      return item.href === '/prestadores';
    }
    if (item.adminOnly) {
      return isAdmin();
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-background transition-colors duration-300">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-900 bg-opacity-50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-card shadow-lg transform transition-transform duration-300 ease-in-out md:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-border bg-card transition-colors duration-300">
          <div className="dark:bg-[#FDFBF7] dark:p-1.5 dark:rounded-lg flex items-center justify-center transition-colors">
            <img 
              src="/logo.png" 
              alt="Logo Identidade Gente" 
              className="h-11 w-auto object-contain"
              style={{ maxWidth: '150px' }}
            />
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        <nav className="mt-6 pb-32 overflow-y-auto flex-1"
             style={{maxHeight: 'calc(100vh - 200px)'}}>
          {filteredNavigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-6 py-3 text-sm font-medium transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-muted/40 ${
                  isActive
                    ? 'bg-primary/10 dark:bg-primary/15 text-primary dark:text-primary border-r-4 border-primary font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* User info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-gray-50 dark:bg-muted/40">
          <div className="flex items-center space-x-3 mb-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                <User className="h-4 w-4" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user?.nome}</p>
              <div className="flex items-center space-x-2">
                <Badge variant={isAdmin() ? "default" : "secondary"} className="text-xs">
                  {isAdmin() ? "Admin" : "Operacional"}
                </Badge>
              </div>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-400"
            onClick={logout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sair
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64">
        {/* Header */}
        <header className="bg-white dark:bg-card shadow-sm border-b">
          <div className="flex items-center justify-between h-16 px-6">
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex-1" />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Sistema de Gestão de Diárias
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// App Component with Authentication
const AppContent = () => {
  const { isAuthenticated, loading, isOperacional } = useAuth();
  const location = useLocation();
  
  // Rotas públicas que não precisam de autenticação
  const publicRoutes = ['/cadastro-prestador', '/esqueci-senha', '/redefinir-senha'];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">Carregando...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated && !isPublicRoute) {
    return <Login />;
  }

  // Se é rota pública, renderizar sem Layout
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/cadastro-prestador" element={<CadastroPrestadorPublico />} />
        <Route path="/esqueci-senha" element={<EsqueciSenha />} />
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />
      </Routes>
    );
  }

  // Operacional: acesso SOMENTE ao cadastro de Prestadores (criar/editar, sem excluir)
  if (isOperacional()) {
    return (
      <Layout>
        <Routes>
          <Route path="/prestadores" element={<Prestadores />} />
          <Route path="*" element={<Navigate to="/prestadores" replace />} />
        </Routes>
      </Layout>
    );
  }

  // Rotas protegidas com Layout
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/clientes" element={
          <ProtectedRoute adminOnly={true}>
            <Clientes />
          </ProtectedRoute>
        } />
        <Route path="/prestadores" element={
          <ProtectedRoute adminOnly={true}>
            <Prestadores />
          </ProtectedRoute>
        } />
        <Route path="/funcoes" element={
          <ProtectedRoute adminOnly={true}>
            <Funcoes />
          </ProtectedRoute>
        } />
        <Route path="/precos" element={
          <ProtectedRoute adminOnly={true}>
            <Precos />
          </ProtectedRoute>
        } />
        <Route path="/diarias" element={
          <ProtectedRoute>
            <Diarias />
          </ProtectedRoute>
        } />
        <Route path="/relatorios" element={
          <ProtectedRoute>
            <Relatorios />
          </ProtectedRoute>
        } />
        <Route path="/custos" element={
          <ProtectedRoute adminOnly={true}>
            <Custos />
          </ProtectedRoute>
        } />
        <Route path="/auditoria" element={
          <ProtectedRoute adminOnly={true}>
            <Auditoria />
          </ProtectedRoute>
        } />
        <Route path="/usuarios" element={
          <ProtectedRoute adminOnly={true}>
            <Usuarios />
          </ProtectedRoute>
        } />
        <Route path="/blacklist" element={
          <ProtectedRoute adminOnly={true}>
            <Blacklist />
          </ProtectedRoute>
        } />
        <Route path="/backups" element={
          <ProtectedRoute adminOnly={true}>
            <Backups />
          </ProtectedRoute>
        } />
        <Route path="/empresa" element={
          <ProtectedRoute adminOnly={true}>
            <Empresa />
          </ProtectedRoute>
        } />
        <Route path="/login" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
          <Toaster />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
